import React, { useState } from "react";
import {
  ActivityIndicator,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import Header from "@/components/ui/Header";
import BottomSheet from "@/components/BottomSheet";
import { ActionButton } from "@/components/ActionButton";
import { router } from "expo-router";
import CommonServices from "@/services/commonServices";
import useUserStore from "@/store/user";

const CANCEL_REASONS = [
  "Too complex",
  "Too expensive",
  "Not useful",
  "Technical issues",
  "Using alternatives",
  "Other",
] as const;

const CancelPlanScreen = () => {
  const { getUserProfile } = useUserStore();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);

  const handleSelectReason = (reason: string) => {
    setSelectedReason(reason);
  };

  const handleNext = () => {
    if (!selectedReason) return;
    setShowFeedbackSheet(true);
  };

  const handleCancelPress = () => {
    router.back();
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackSheet(false);
    setFeedback("");
  };

  const handleFeedbackSubmit = async () => {
    try {
      setIsLoading(true);
      setShowFeedbackSheet(false);

      // Check if user is eligible for retention offer
      const previewRes = await CommonServices.cancelPlanPreview();

      if (
        previewRes.success &&
        previewRes.data?.eligible &&
        previewRes.data?.offer
      ) {
        // Navigate to offer screen with data
        setIsLoading(false);
        router.push({
          pathname: "/account/cancel/offer",
          params: {
            reason: selectedReason || "",
            feedback: feedback.trim(),
            offerData: JSON.stringify(previewRes.data.offer),
          },
        });
        return;
      }

      // Not eligible, proceed with cancellation
      await performCancellation();
    } catch (error) {
      console.error("Error checking eligibility:", error);
      // On error, just proceed with cancellation
      await performCancellation();
    }
  };

  const performCancellation = async () => {
    try {
      setIsLoading(true);
      const res = await CommonServices.cancelSubscription({
        reason: selectedReason || "Unknown",
        other: feedback.trim() || undefined,
      });

      if (res.success) {
        await getUserProfile();
        // Navigate to success screen
        router.replace("/account/cancel/success");
      } else {
        console.error("Cancel subscription failed:", res.message);
      }
    } catch (error) {
      console.error("Cancel subscription error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="" showBackButton={true} />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Why are you canceling?</Text>

        <View style={styles.optionsContainer}>
          {CANCEL_REASONS.map((reason, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.optionButton,
                selectedReason === reason && styles.optionButtonSelected,
              ]}
              onPress={() => handleSelectReason(reason)}
              disabled={isLoading}
            >
              <Text style={styles.optionText}>{reason}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      <View style={styles.bottomSection}>
        <View style={styles.buttonsRow}>
          <ActionButton
            title="Next"
            type="secondary"
            onPress={handleNext}
            disabled={!selectedReason || isLoading}
            style={styles.actionButton}
          />
          <ActionButton
            title="Cancel"
            type="primary"
            onPress={handleCancelPress}
            disabled={isLoading}
            style={styles.actionButton}
          />
        </View>
      </View>

      {/* Feedback BottomSheet */}
      <BottomSheet
        isVisible={showFeedbackSheet}
        onClose={handleFeedbackCancel}
        height={320}
        enableBackdropDismiss
        enableSwipeDown
      >
        <View style={styles.feedbackContainer}>
          <Text style={styles.feedbackTitle}>Tell us how we can improve</Text>

          <View style={styles.feedbackInputContainer}>
            <TextInput
              style={styles.feedbackInput}
              placeholder="Describe your issue..."
              placeholderTextColor="#9CA3AF"
              multiline
              value={feedback}
              onChangeText={setFeedback}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.feedbackButtonsContainer}>
            <ActionButton
              title="Cancel"
              type="hide"
              onPress={handleFeedbackCancel}
              style={styles.feedbackButton}
            />
            <ActionButton
              title={isLoading ? "Submitting..." : "Submit"}
              type="primary"
              onPress={handleFeedbackSubmit}
              disabled={isLoading}
              style={styles.feedbackButton}
              icon={
                isLoading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : undefined
              }
            />
          </View>
        </View>
      </BottomSheet>
    </SafeAreaView>
  );
};

export default CancelPlanScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 24,
    lineHeight: 37,
  },
  optionsContainer: {
    gap: 10,
  },
  optionButton: {
    backgroundColor: "#F3F3F3",
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 19,
    minHeight: 64,
    justifyContent: "center",
    alignItems: "center",
  },
  optionButtonSelected: {
    backgroundColor: "#F3F3F3",
    borderWidth: 1,
    borderColor: "#7EAD87",
  },
  optionText: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    letterSpacing: -0.36,
    lineHeight: 23,
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 34,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#F3F7F3",
    backgroundColor: Colors.background,
  },
  buttonsRow: {
    flexDirection: "row",
    gap: 12,
  },
  actionButton: {
    flex: 1,
    height: 58,
  },

  // Feedback BottomSheet styles
  feedbackContainer: {
    flex: 1,
  },
  feedbackTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    textAlign: "center",
    marginBottom: 16,
    color: "#000",
    letterSpacing: -0.43,
  },
  feedbackInputContainer: {
    backgroundColor: "#F3F3F3",
    borderRadius: 16,
    padding: 20,
    minHeight: 153,
    marginBottom: 16,
  },
  feedbackInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    minHeight: 100,
  },
  feedbackButtonsContainer: {
    flexDirection: "row",
    gap: 10,
  },
  feedbackButton: {
    flex: 1,
    height: 48,
  },
});
